import { Link } from "react-router-dom";
import { Check, ArrowRight, Scale, ShieldCheck } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PLANS } from "@/sections/PricingTeaser";
import { track } from "@/lib/analytics";

const FACTORS = [
  "Property type",
  "Property location",
  "Monthly rent",
  "Number of properties",
  "Services required",
  "Tenant management requirements",
  "Inspection frequency",
  "Maintenance involvement",
  "Vacancy / make-ready requirements",
];

const PROMISES = [
  "No hidden maintenance markup without prior disclosure.",
  "No unnecessary work without owner approval — except genuine emergency situations where immediate action is necessary to prevent further damage.",
  "Every charge shown clearly before work begins.",
];

export default function Pricing() {
  return (
    <>
      <SEO
        title="Transparent Pricing | NRI Property Management Hyderabad"
        description="Flexible property management pricing for NRI owners in Hyderabad — Property Care, Rental Management and Complete Property Management. Case-to-case, transparent quotes."
        path="/pricing"
      />
      <PageHero
        eyebrow="Transparent Pricing"
        titleLines={["Every Property Is Different.", "So Is Every Quote."]}
        copy="A 2BHK apartment with a stable tenant needs a different level of involvement than a vacant villa requiring repairs, painting and tenant placement. We price the scope — not a template."
        ctaLabel="Request My Property Management Quote"
      />

      <section className="bg-ivory py-24 sm:py-32" data-testid="pricing-plans">
        <div className="container-site">
          <Stagger className="grid gap-5 lg:grid-cols-3" gap={0.09}>
            {PLANS.map((p, i) => (
              <StaggerItem key={p.name}>
                <div
                  className={`relative flex h-full flex-col rounded-3xl p-8 sm:p-10 ${
                    p.featured
                      ? "bg-navy-900 text-white shadow-[0_35px_70px_-25px_rgba(10,17,40,0.55)]"
                      : "border border-navy-900/10 bg-white"
                  }`}
                  data-testid={`pricing-plan-${i + 1}`}
                >
                  {p.featured && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold-500 px-4 py-1.5 font-display text-[10px] font-extrabold uppercase tracking-[0.2em] text-navy-900">
                      Most Chosen
                    </span>
                  )}
                  <span className={`font-display text-xs font-bold tracking-[0.3em] ${p.featured ? "text-gold-400" : "text-gold-600"}`}>
                    Plan {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className={`mt-3 font-display text-2xl font-extrabold tracking-tight ${p.featured ? "text-white" : "text-navy-900"}`}>
                    {p.name}
                  </h3>
                  <p className={`mt-2 text-sm ${p.featured ? "text-white/60" : "text-muted-foreground"}`}>{p.forWhom}</p>
                  <ul className="mt-7 flex-1 space-y-3">
                    {p.includes.map((item) => (
                      <li key={item} className={`flex items-start gap-2.5 text-sm ${p.featured ? "text-white/85" : "text-navy-900/80"}`}>
                        <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-gold-400" : "text-gold-600"}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className={`mt-8 border-t pt-6 font-display text-xs font-bold uppercase tracking-[0.2em] ${p.featured ? "border-white/10 text-gold-400" : "border-navy-900/10 text-gold-600"}`}>
                    {p.pricing}
                  </p>
                  <Link
                    to="/contact"
                    className={`${p.featured ? "btn-gold" : "btn-outline-navy"} mt-6 w-full`}
                    data-testid={`pricing-plan-cta-${i + 1}`}
                    onClick={() => track("quote_request", { plan: p.name })}
                  >
                    Request a Quote <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="border-y border-navy-900/10 bg-white py-24 sm:py-32">
        <div className="container-site grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHeading
              eyebrow="How Your Quote Is Calculated"
              title="Pricing Based on Reality, Not Packages."
              copy="Your proposal is shaped by what your property actually needs:"
            />
            <Stagger className="mt-8 grid gap-3 sm:grid-cols-2" gap={0.05}>
              {FACTORS.map((f) => (
                <StaggerItem key={f}>
                  <div className="flex items-center gap-3 rounded-xl border border-navy-900/10 bg-ivory px-4 py-3.5">
                    <Scale className="h-4 w-4 shrink-0 text-gold-600" />
                    <span className="text-sm font-medium text-navy-900/85">{f}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <div className="flex flex-col justify-center">
            <Reveal>
              <h3 className="font-display text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
                Competitive. Transparent. Built for Value.
              </h3>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                We position ourselves as competitive, transparent and designed to deliver better value. We aim
                to offer highly competitive pricing — typically targeting 5–10% better value than comparable
                services for equivalent scope — while maintaining transparent service standards.
              </p>
              <ul className="mt-8 space-y-4">
                {PROMISES.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-status" />
                    <span className="text-sm leading-relaxed text-navy-900/80">{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection
        title="Get a Quote Built Around Your Property."
        copy="Tell us about your property and what you need handled. We'll come back with a clear scope, transparent charges and a timeline."
      />
    </>
  );
}
